import { appUrl } from "Utils/UrlUtils";

// Stream Chat identities are keyed by IdentityUser.PublicID (uuid) — one chat user per role
// grant, so one person's Customer/Merchant/Hunter roles are three separate chat users.
// The stream user id IS the public id; this named seam documents that equivalence and keeps
// a single place to change if the scheme ever does.
export const identityUserPublicIdFromStreamId = (
  streamUserId: string,
): string => streamUserId;

/**
 * Where a chat participant's public profile lives. Always addressed by persona PublicID, never by
 * handle: Stream carries only disclosable identity (ADR-0007) and no handle at all, and
 * /profile/{key} takes either. Callers MUST gate this on the channel not being anonymised
 * (ADR-0008) — a blocked pair's counterpart is deliberately unidentifiable, and a link is an
 * identity.
 */
export const getChatMemberProfileUrl = (streamUserId: string): string =>
  `${appUrl("public-profile")}/${identityUserPublicIdFromStreamId(streamUserId)}`;

/**
 * Vertical space above each part of the people picker — the search field, the selected-members
 * row, the results. Owned by the parts rather than applied as a Stack gap: the members row
 * collapses to nothing, and a gap survives zero height only to vanish in one step when the element
 * leaves the layout, which reads as a jump at the end of the close.
 */
export const CHAT_PICKER_SECTION_SPACING = 3;

/**
 * How tall the message field may grow before it scrolls internally. One line when empty, then it
 * follows the content. Shared by the channel composer, the thread composer and the edit form so
 * all three feel like the same control.
 */
export const MESSAGE_COMPOSER_MAX_ROWS = 5;

/**
 * Manual threading is switched off for this version of the chat (decision 2026-10-07, together
 * with "Find in conversation", which navigates the conversation window only). Gates the "reply"
 * message action and the replies-count button under a message, i.e. the two ways a person could
 * START or discover a thread by hand. Flip to true to bring both back; quoted replies are
 * unaffected either way.
 *
 * The <Thread> panel and the ?thread URL sync are NOT gated: threads carry the payments made
 * against a multi-payer payment request (posted as replies by the payment drawer), and the
 * request card is the one entry point ("N payments"). The thread panel has no composer at all
 * (ChatPaymentThread) so such a thread cannot turn into a conversation.
 */
export const IS_MANUAL_THREADING_ENABLED = false;
